import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import { tap } from 'rxjs/operators';
import { AdditionalMaterialPayload } from '../shared/models/material.model';
import { BillOfMaterialPayload, MaterialCatalogItemPayload, MaterialFilePayload } from '../shared/models/material-catalog-item.model';
import { WarehousePayload } from '../shared/models/warehouse.model';

@Injectable({
  providedIn: 'root',
})
export class WarehouseService {
  apiURL: string = environment.apiURL;
  fileType: string | null = null;

  constructor(private http: HttpClient) {}

  /**
   * Get list of materials
   */
  getWarehouseListOfMaterials(data: {
    skip?: number;
    limit?: number;
    search?: string;
    sector?: string;
    fromDate?: string;
    toDate?: string;
  }): Observable<any> {
    let params = new HttpParams();

    if (data.skip !== undefined) params = params.set('skip', data.skip);
    if (data.limit !== undefined) params = params.set('limit', data.limit);
    if (data.search) params = params.append('search', data.search);
    if (data.sector) params = params.append('sector', data.sector);
    if (data.fromDate) params = params.append('fromDate', data.fromDate);
    if (data.toDate) params = params.append('toDate', data.toDate);

    return this.http.get(`${this.apiURL}/warehouse/materials`, { params });
  }

  /**
   * Get list of orders
   */
  getWarehouseListOfOrders(data: {
    skip?: number;
    limit?: number;
    search?: string;
    sector?: string;
    fromDate?: string;
    toDate?: string;
    isReturned?: boolean;
  }): Observable<any> {
    let params = new HttpParams();

    if (data.skip !== undefined) params = params.set('skip', data.skip);
    if (data.limit !== undefined) params = params.set('limit', data.limit);
    if (data.search) params = params.append('search', data.search);
    if (data.sector) params = params.append('sector', data.sector);
    if (data.fromDate) params = params.append('fromDate', data.fromDate);
    if (data.toDate) params = params.append('toDate', data.toDate);
    if (data.isReturned !== undefined && data.isReturned !== null) params = params.append('isReturned', data.isReturned);

    return this.http.get(`${this.apiURL}/warehouse/work-orders`, { params });
  }

  /**
   * Get list of returned orders
   */
  getWarehouseListOfReturnedOrders(data: { skip?: number; limit?: number; search?: string; sector?: string }): Observable<any> {
    let params = new HttpParams().set('isReturned', 'true');

    if (data.skip !== undefined) params = params.set('skip', data.skip);
    if (data.limit !== undefined) params = params.set('limit', data.limit);
    if (data.search) params = params.append('search', data.search);
    if (data.sector) params = params.append('sector', data.sector);

    return this.http.get(`${this.apiURL}/warehouse/work-orders`, { params });
  }

  /**
   * Edit alternative material
   */
  editAlternativeMaterial(orderMaterialId: string, data: any): Observable<any> {
    return this.http.patch(`${this.apiURL}/warehouse/materials/${orderMaterialId}/alternative-material`, data);
  }

  /**
   * Edit procurement comment
   */
  editProcurementComment(orderMaterialId: string, data: any): Observable<any> {
    return this.http.patch(`${this.apiURL}/warehouse/materials/${orderMaterialId}/procurement-comment`, data);
  }

  /**
   * Reserve material
   */
  reserveMaterial(orderMaterialId: string): Observable<any> {
    return this.http.patch(`${this.apiURL}/warehouse/materials/${orderMaterialId}/reserve-material`, {});
  }

  /**
   * Return material
   */
  returnMaterial(orderMaterialId: string): Observable<any> {
    return this.http.patch(`${this.apiURL}/warehouse/work-orders/materials/${orderMaterialId}/return-material`, {});
  }

  /**
   * Issuance request material
   */
  issuanceRequestMaterial(orderMaterialId: string): Observable<any> {
    return this.http.patch(`${this.apiURL}/warehouse/work-orders/materials/${orderMaterialId}/issuance-request`, {});
  }

  /**
   * Return all materials
   */
  returnAllMaterials(orderId: string): Observable<any> {
    return this.http.patch(`${this.apiURL}/warehouse/work-orders/${orderId}/return-materials`, {});
  }

  /**
   * Download document
   */
  downloadDocument(orderId: string, sector?: string): Observable<any> {
    let params = new HttpParams();

    if (sector) params = params.append('sector', sector);

    return this.http
      .get(`${this.apiURL}/warehouse/work-orders/${orderId}/download`, {
        params: params,
        responseType: 'blob',
        observe: 'body',
      })
      .pipe(
        tap(res => {
          this.fileType = res.type;
        }),
      );
  }

  /**
   * Issue material
   */
  issueMaterial(orderMaterialId: string, data: any): Observable<any> {
    return this.http.post(`${this.apiURL}/warehouse/work-orders/materials/${orderMaterialId}/issue-material`, data);
  }

  /**
   * Submit additional material
   */
  submitAdditionalMaterial(orderId: string, data: AdditionalMaterialPayload): Observable<any> {
    return this.http.post(`${this.apiURL}/warehouse/work-orders/${orderId}/additional-material`, data);
  }

  /**
   * Delete material
   */
  deleteMaterial(orderId: string, materialId: string): Observable<any> {
    return this.http.delete(`${this.apiURL}/work-orders/${orderId}/materials/${materialId}`);
  }

  /**
   * Get order operations
   */
  getOrderOperations(orderId: string): Observable<any> {
    return this.http.get(`${this.apiURL}/work-orders/${orderId}/operations`);
  }

  /**
   * Correction of material issuance
   */
  correctionOfMaterialIssuance(orderMaterialId: string, data: any): Observable<any> {
    return this.http.patch(`${this.apiURL}/warehouse/work-orders/materials/${orderMaterialId}/correct-amount`, data);
  }

  /**
   * Get warehouse stock (lager)
   */
  getWarehouseStock(data: {
    skip?: number;
    limit?: number;
    search?: string;
    sortField?: string;
    sortValue?: string;
    group?: string;
    warehouseId: string;
  }): Observable<any> {
    const queryParams: Record<string, string | number> = {};

    if (data.skip !== null && data.skip !== undefined) queryParams['skip'] = data.skip;
    if (data.limit !== null && data.limit !== undefined) queryParams['limit'] = data.limit;
    if (data.search) queryParams['search'] = data.search;
    if (data.group) queryParams['group'] = data.group;
    if (data.sortValue && data.sortField) queryParams[data.sortField] = data.sortValue;

    const params = new HttpParams({ fromObject: queryParams });

    return this.http.get(`${this.apiURL}/warehouse-stock/warehouses/${data.warehouseId}/stock`, { params });
  }

  /**
   * Get materials from upis
   */
  getListOfMaterialsFromUpis(data: {
    skip?: number;
    limit?: number;
    search?: string;
    sortField?: string;
    sortValue?: string;
  }): Observable<any> {
    const queryParams: Record<string, string | number> = {};

    if (data.skip !== null && data.skip !== undefined) queryParams['skip'] = data.skip;
    if (data.limit !== null && data.limit !== undefined) queryParams['limit'] = data.limit;
    if (data.search) queryParams['search'] = data.search;
    if (data.sortValue && data.sortField) queryParams[data.sortField] = data.sortValue;

    let params = new HttpParams({ fromObject: queryParams });

    return this.http.get(`${this.apiURL}/warehouse/materials/upis`, { params });
  }

  /**
   * Get catalog items
   */
  getMaterialCatalogItems(data: { skip?: number; limit?: number; search?: string }): Observable<any> {
    let queryParams = {};

    if (data.skip !== null && data.skip !== undefined) queryParams['skip'] = data.skip;
    if (data.limit !== null && data.limit !== undefined) queryParams['limit'] = data.limit;
    if (data.search) queryParams['search'] = data.search;
    // if (data.filter1) queryParams['filter1'] = data.filter1;

    let params = new HttpParams({ fromObject: queryParams });

    return this.http.get(`${this.apiURL}/warehouse-stock/materials`, { params });
  }

  /**
   * Get catalog item by id
   */
  getMaterialCatalogItem(materialId: string): Observable<any> {
    return this.http.get(`${this.apiURL}/warehouse-stock/materials/${materialId}`);
  }

  /**
   * Create catalog item
   */
  createMaterialCatalogItem(data: MaterialCatalogItemPayload): Observable<any> {
    return this.http.post(`${this.apiURL}/warehouse-stock/materials`, data);
  }

  /**
   * Edit catalog item
   */
  editMaterialCatalogItem(materialId: string, data: MaterialCatalogItemPayload): Observable<any> {
    return this.http.put(`${this.apiURL}/warehouse-stock/materials/${materialId}`, data);
  }

  /**
   * Delete catalog item
   */
  deleteMaterialCatalogItem(materialId: string): Observable<any> {
    return this.http.delete(`${this.apiURL}/warehouse-stock/materials/${materialId}`);
  }

  /**
   * Get material files
   */
  getMaterialFiles(materialId: string): Observable<any> {
    return this.http.get(`${this.apiURL}/warehouse-stock/materials/${materialId}/files`);
  }

  /**
   * Add material file
   */
  addMaterialFile(materialId: string, data: MaterialFilePayload): Observable<any> {
    return this.http.post(`${this.apiURL}/warehouse-stock/materials/${materialId}/files`, data);
  }

  /**
   * Delete material file
   */
  deleteMaterialFile(materialId: string, fileId: string): Observable<any> {
    return this.http.delete(`${this.apiURL}/warehouse-stock/materials/${materialId}/files/${fileId}`);
  }

  /**
   * Get bill of materials for a material
   */
  getBillOfMaterials(materialId: string): Observable<any> {
    return this.http.get(`${this.apiURL}/warehouse-stock/materials/${materialId}/bom`);
  }

  /**
   * Create bill of material item
   */
  createBillOfMaterial(materialId: string, data: BillOfMaterialPayload): Observable<any> {
    return this.http.post(`${this.apiURL}/warehouse-stock/materials/${materialId}/bom`, data);
  }

  /**
   * Edit bill of material item
   */
  editBillOfMaterial(materialId: string, bomItemId: number, data: BillOfMaterialPayload): Observable<any> {
    return this.http.patch(`${this.apiURL}/warehouse-stock/materials/${materialId}/bom/${bomItemId}`, data);
  }

  /**
   * Copy bill of materials from another material
   */
  copyBillOfMaterials(materialId: string, fromMaterialId: string): Observable<any> {
    return this.http.post(`${this.apiURL}/warehouse-stock/materials/${materialId}/bom/copy`, { fromMaterialId });
  }

  /**
   * Delete bill of material item
   */
  deleteBillOfMaterial(materialId: string, bomItemId: number): Observable<any> {
    return this.http.delete(`${this.apiURL}/warehouse-stock/materials/${materialId}/bom/${bomItemId}`);
  }

  /**
   * Import material catalog from XLSX
   */
  importMaterialCatalog(file: File): Observable<any> {
    const formData = new FormData();
    formData.append('file', file);
    return this.http.post(`${this.apiURL}/warehouse-stock/materials/import`, formData);
  }

  /**
   * Export warehouse stock
   */
  exportWarehouseStock(warehouseId: string, format: string = 'xlsx'): Observable<any> {
    const params = new HttpParams().set('format', format);

    return this.http.get(`${this.apiURL}/warehouse-stock/warehouses/${warehouseId}/stock/export`, {
      params,
      responseType: 'blob',
      observe: 'body',
    });
  }

  /**
   * Upload materials from file
   */
  uploadMaterials(warehouseId: string, data: FormData): Observable<any> {
    return this.http.post(`${this.apiURL}/warehouse-stock/warehouses/${warehouseId}/entries/import`, data);
  }

  /**
   * Get warehouse article by code
   */
  getWarehouseArticleByCode(warehouseId: string, code: string): Observable<any> {
    const params = new HttpParams({ fromObject: { code, limit: 1 } });
    return this.http.get(`${this.apiURL}/warehouse-stock/warehouses/${warehouseId}/stock`, { params });
  }

  /**
   * Create new warehouse stock item
   */
  createNewWarehouseStockItem(
    warehouseId: string,
    data: {
      supplier?: string;
      entryDate: string;
      items: Array<{
        materialId: number;
        packageCode: string;
        barcode: string;
        quantity: number;
        unitPrice: number;
      }>;
    },
    fromId?: string,
  ): Observable<any> {
    let params = new HttpParams();

    if (fromId) {
      params = params.set('fromId', fromId);
    }

    return this.http.post(`${this.apiURL}/warehouse-stock/warehouses/${warehouseId}/entries`, data, { params });
  }

  /**
   * Adjust warehouse stock item quantity
   */
  adjustWarehouseStockItem(
    stockItemId: string,
    data: { quantity: number; typeOfTransfer: 'Ulaz' | 'Izlaz'; reason?: string },
  ): Observable<any> {
    return this.http.patch(`${this.apiURL}/warehouse-stock/stock-items/${stockItemId}/adjust`, data);
  }

  /**
   * Get warehouse stock item details
   */
  getWarehouseStockItemDetails(stockItemId: string): Observable<any> {
    return this.http.get(`${this.apiURL}/warehouse-stock/stock-items/${stockItemId}`);
  }

  /**
   * Create warehouse stock transfer
   */
  createWarehouseStockTransfer(
    data: {
      fromWarehouseId: number;
      toWarehouseId: number;
      transferDate: string;
      items: Array<{
        stockItemId: number;
        quantity: number;
      }>;
    },
    fromId?: number,
  ): Observable<any> {
    let params = new HttpParams();

    if (fromId !== undefined && fromId !== null) {
      params = params.set('fromId', fromId);
    }

    return this.http.post(`${this.apiURL}/warehouse-stock/transfers`, data, { params });
  }

  /**
   * Get warehouse list
   */
  getWarehouseList(data: { skip?: number; limit?: number; search?: string; location?: string } = {}): Observable<any> {
    let params = new HttpParams();

    if (data.skip !== undefined && data.skip !== null) params = params.set('skip', data.skip);
    if (data.limit !== undefined && data.limit !== null) params = params.set('limit', data.limit);

    if (data.search) params = params.append('search', data.search);
    if (data.location) params = params.append('location', data.location);

    return this.http.get(`${this.apiURL}/warehouse-stock/warehouses`, { params });
  }

  /**
   * Get warehouse details
   */
  getWarehouseDetails(warehouseId: string): Observable<any> {
    return this.http.get(`${this.apiURL}/warehouse-stock/warehouses/${warehouseId}`);
  }

  /**
   * Create warehouse
   */
  createWarehouse(data: WarehousePayload): Observable<any> {
    return this.http.post(`${this.apiURL}/warehouse-stock/warehouses`, data);
  }

  /**
   * Edit warehouse
   */
  updateWarehouse(warehouseId: string, data: WarehousePayload): Observable<any> {
    return this.http.put(`${this.apiURL}/warehouse-stock/warehouses/${warehouseId}`, data);
  }

  /**
   * Delete warehouse
   */
  deleteWarehouse(warehouseId: string): Observable<any> {
    return this.http.delete(`${this.apiURL}/warehouse-stock/warehouses/${warehouseId}`);
  }
}
